// Spørsmål om sykkelkort når en elsykkelservice uten sykkelkort åpnes (vinnsel, 05.11).
// Målet er at flere elsykler får sykkelkort. Har kunden sykler fra før, foreslås de; ellers
// registreres et nytt kort med bare merke og modell (resten fylles ut fra kundekortet senere).
// «Avstå» lagres på servicen (sykkelkortAvstatt), så spørsmålet ikke kommer igjen der.
import React, { useEffect, useState } from "react";
import { useDialog } from "../tilgjengelighet";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

async function json(r, feil) {
  const b = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(b?.message || feil);
  return b;
}

/**
 * @param {object} props
 * @param {object} props.svc          servicen (trenger _id, customerId og produkt)
 * @param {string} props.phoneKey     kundens nummer (normalisert), kreves for nytt sykkelkort
 * @param {string} props.ansatt       den valgte ansatte (fullt navn), lagres ved «Avstå»
 * @param {(svc: object, bikeId: string) => void} props.onKoblet  sykkelkortet er koblet til servicen
 * @param {(svc: object) => void} props.onAvstatt                  ansatt avsto; servicen er oppdatert
 * @param {() => void} props.onLukk                                «Senere»: spør igjen neste gang
 */
export default function SykkelkortForslag({ svc, phoneKey, ansatt, onKoblet, onAvstatt, onLukk }) {
  const dialogRef = useDialog(true, onLukk);
  const [sykler, setSykler] = useState(null); // null = henter
  const [merke, setMerke] = useState(svc?.produkt?.merke || "");
  const [modell, setModell] = useState(svc?.produkt?.modell || "");
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState("");

  useEffect(() => {
    let avbrutt = false;
    const sok = svc?.customerId
      ? `customerId=${encodeURIComponent(svc.customerId)}`
      : phoneKey
        ? `phoneKey=${encodeURIComponent(phoneKey)}`
        : "";
    if (!sok) {
      setSykler([]);
      return undefined;
    }
    fetch(`${API_BASE_URL}/bikes?${sok}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((liste) => {
        if (avbrutt) return;
        // Elsykler først, så de kortene som mest sannsynlig er denne sykkelen.
        const sortert = (Array.isArray(liste) ? liste : []).sort(
          (a, b) => Number(b.elsykkel === true) - Number(a.elsykkel === true)
        );
        setSykler(sortert);
      })
      .catch(() => !avbrutt && setSykler([]));
    return () => { avbrutt = true; };
  }, [svc?.customerId, phoneKey]);

  async function kobleTil(bikeId) {
    const oppdatert = await json(
      await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/bike`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bikeId }),
      }),
      "Kunne ikke koble sykkelkortet til servicen."
    );
    onKoblet(oppdatert, bikeId);
  }

  async function brukSykkel(bikeId) {
    setFeil("");
    setOpptatt(true);
    try {
      await kobleTil(bikeId);
    } catch (e) {
      setFeil(e.message);
    } finally {
      setOpptatt(false);
    }
  }

  async function opprett(e) {
    e.preventDefault();
    setFeil("");
    if (!merke.trim() || !modell.trim()) {
      setFeil("Fyll inn både merke og modell.");
      return;
    }
    if (!phoneKey) {
      setFeil("Servicen har ikke telefonnummer, så sykkelkortet kan ikke opprettes her.");
      return;
    }
    setOpptatt(true);
    try {
      // Kunde-ID og service, så sykkelkortet kobles til kunden (og vises på Din side).
      const sykkel = await json(
        await fetch(`${API_BASE_URL}/bikes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneKey,
            customerId: svc?.customerId,
            serviceId: svc?._id,
            merke: merke.trim(),
            modell: modell.trim(),
            elsykkel: true,
          }),
        }),
        "Kunne ikke opprette sykkelkortet."
      );
      await kobleTil(sykkel._id);
    } catch (e2) {
      setFeil(e2.message);
    } finally {
      setOpptatt(false);
    }
  }

  async function avsta() {
    setFeil("");
    setOpptatt(true);
    try {
      const oppdatert = await json(
        await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/sykkelkort-avsta`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ansatt }),
        }),
        "Kunne ikke lagre valget."
      );
      onAvstatt(oppdatert);
    } catch (e) {
      setFeil(e.message);
    } finally {
      setOpptatt(false);
    }
  }

  const harForslag = Array.isArray(sykler) && sykler.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sykkelkort-forslag-tittel"
        aria-describedby="sykkelkort-forslag-tekst"
        className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 id="sykkelkort-forslag-tittel" className="text-base font-bold text-slate-900">
          Sykkelkort for elsykkelen
        </h2>
        <p id="sykkelkort-forslag-tekst" className="mt-1 text-sm text-slate-700">
          Servicen har ikke sykkelkort.{" "}
          {harForslag
            ? "Kunden har sykler fra før. Velg riktig sykkel, eller registrer en ny."
            : "Registrer merke og modell, så kommer sykkelen på kundekortet."}
        </p>

        {sykler === null && <p className="mt-4 text-sm text-slate-600">Henter kundens sykler …</p>}

        {harForslag && (
          <ul className="mt-4 space-y-2" aria-label="Kundens sykler">
            {sykler.map((b) => (
              <li
                key={b._id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0 [overflow-wrap:anywhere]">
                  <div className="font-medium text-slate-900">
                    {[b.merke, b.modell].filter(Boolean).join(" ") || "Sykkel uten navn"}
                  </div>
                  <div className="text-xs text-slate-600">
                    {[b.elsykkel === true ? "Elsykkel" : null, b.årsmodell, b.shortId].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={opptatt}
                  onClick={() => brukSykkel(b._id)}
                  className="shrink-0 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
                >
                  Bruk denne<span className="sr-only">: {[b.merke, b.modell].filter(Boolean).join(" ")}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {sykler !== null && (
          <form onSubmit={opprett} className="mt-4">
            {harForslag && <h3 className="mb-2 text-sm font-semibold text-slate-800">Eller registrer ny sykkel</h3>}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <label htmlFor="sykkelkort-forslag-merke" className="block text-sm font-medium text-slate-700">
                  Merke
                </label>
                <input
                  id="sykkelkort-forslag-merke"
                  value={merke}
                  onChange={(e) => { setMerke(e.target.value); setFeil(""); }}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
              <div className="min-w-0">
                <label htmlFor="sykkelkort-forslag-modell" className="block text-sm font-medium text-slate-700">
                  Modell
                </label>
                <input
                  id="sykkelkort-forslag-modell"
                  value={modell}
                  onChange={(e) => { setModell(e.target.value); setFeil(""); }}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={opptatt}
              className="mt-3 w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Opprett sykkelkort
            </button>
          </form>
        )}

        {feil && (
          <p role="alert" className="mt-3 text-sm font-semibold text-rose-800">
            {feil}
          </p>
        )}

        <div className="mt-5 flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-4">
          <button
            type="button"
            disabled={opptatt}
            onClick={avsta}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            Avstå på denne servicen
          </button>
          <button
            type="button"
            onClick={onLukk}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800 hover:bg-slate-200"
          >
            Senere
          </button>
        </div>
      </div>
    </div>
  );
}
